import { z } from "zod";
import { messageKindSchema, SERVER_ONLY_KINDS } from "../message-payload";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

// Per-kind payload is validated by the builder layer inside the mutator.
// At the HTTP contract level we accept an opaque JSON object (or null) —
// strict shape enforcement lives in `packages/shared/src/message-builders.ts`
// + `packages/shared/src/message-payload.ts`.
const messagePayloadBody = z
  .record(z.string(), z.unknown())
  .nullable()
  .optional();

// HTTP callers must NOT be able to stamp SERVER_ONLY_KINDS (system +
// call_*). The mutator's `assertClientMaySetKind` only fires on the
// client transaction branch; `runActionMutator` invokes mutators
// server-side, so the gate never runs on the HTTP path. Reject at the
// schema boundary — the 400 fires before the mutator is invoked.
const clientAuthorableKind = messageKindSchema.refine(
  (k) => !SERVER_ONLY_KINDS.has(k),
  { message: "kind is server-only and cannot be set via the HTTP API" },
);

export const createMessageBodySchema = z.object({
  channel_id: z.string().min(1),
  // Relaxed: media-only messages (image with no caption) carry empty content.
  // Clients still send an empty string, never omit the field.
  content: z.string(),
  kind: clientAuthorableKind.optional(),
  payload: messagePayloadBody,
  mentions: z.array(z.string().min(1)).optional(),
  message_id: z.string().min(1).optional(),
});

export const replyToMessageBodySchema = z.object({
  channel_id: z.string().min(1),
  content: z.string(),
  kind: clientAuthorableKind.optional(),
  payload: messagePayloadBody,
  mentions: z.array(z.string().min(1)).optional(),
  is_broadcast: z.boolean().optional(),
});

// `kind` is IMMUTABLE after insert. The edit schema explicitly rejects it
// so a client sending `{ content: "x", kind: "system" }` gets a 400 instead
// of a silent overwrite of the message's kind.
export const editMessageBodySchema = z
  .object({
    content: z.string().optional(),
    payload: messagePayloadBody,
    mentions: z.array(z.string().min(1)).optional(),
  })
  .strict();

export const messagesWriteSuccessSchema = z.object({
  ok: z.literal(true),
});

export const createMessageSuccessSchema = messagesWriteSuccessSchema.extend({
  message_id: z.string().min(1),
});

async function parseMessagesWriteSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof messagesWriteSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = messagesWriteSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

async function parseCreateMessageSuccess(
  response: Response,
  fallbackMessage: string,
): Promise<z.infer<typeof createMessageSuccessSchema>> {
  const payload =
    typeof response.json === "function"
      ? await response.json().catch(() => null)
      : null;
  const parsed = createMessageSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError(fallbackMessage, 500, {
      issues: parsed.error.issues,
    });
  }
  return parsed.data;
}

export function createMessagePath(): string {
  return "/api/messages";
}

export function replyToMessagePath(messageId: string): string {
  return `/api/messages/${encodeURIComponent(messageId)}/replies`;
}

export function editMessagePath(messageId: string): string {
  return `/api/messages/${encodeURIComponent(messageId)}`;
}

export async function createMessageRequest({
  apiBaseUrl,
  accessToken,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  body: z.input<typeof createMessageBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof createMessageSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = createMessageBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${createMessagePath()}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to create message");
  }

  return parseCreateMessageSuccess(response, "Invalid create message response");
}

export async function replyToMessageRequest({
  apiBaseUrl,
  accessToken,
  messageId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  messageId: string;
  body: z.input<typeof replyToMessageBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof messagesWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = replyToMessageBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${replyToMessagePath(messageId)}`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to reply to message");
  }

  return parseMessagesWriteSuccess(response, "Invalid reply message response");
}

export async function editMessageRequest({
  apiBaseUrl,
  accessToken,
  messageId,
  body,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  messageId: string;
  body: z.input<typeof editMessageBodySchema>;
  fetchImpl?: FetchLike;
}): Promise<z.infer<typeof messagesWriteSuccessSchema>> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = editMessageBodySchema.parse(body);
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${editMessagePath(messageId)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Failed to edit message");
  }

  return parseMessagesWriteSuccess(response, "Invalid edit message response");
}
